/**
 * 工單維修單（be doc dd7f0319 #42／#44／#45／#46）。
 * - 已核准工單（PROCESSING／CLOSED／ABORTED）可新開、修改未結、結單。
 * - 結單：problemQty＝repairedQty＋notRepairedQty，同交易扣用料庫存。
 * - 結單後只能「更正」：選用料全額沖回（一次），立即過帳，不可再改。
 * - notRepairedQty ≠ 工單報廢量，兩者不互相影響。
 */
import { extractUnitOptions } from '#/modules/imsMgmt/helper/materialUnit/extractUnitOptions';
import { getInvStockAvailableQty } from '#/modules/imsMgmt/receipts/stockTransfer/helper/formSerialize';
import { getWorkOrderApiErrorMessage } from '#/modules/momMgmt/workOrder/helper/apiError';
import { WORK_ORDER_STATUS } from '#/modules/momMgmt/workOrder/helper/workOrderStatus';

export const WORK_ORDER_REPAIR_STATUS = {
  OPEN: 'OPEN',
  CLOSED: 'CLOSED',
};

const STATUS_LABELS = {
  OPEN: '未結',
  CLOSED: '已結',
};

export function formatWorkOrderRepairStatus(status) {
  return STATUS_LABELS[status] ?? status ?? '—';
}

const REPAIRABLE_PARENT_STATUSES = [
  WORK_ORDER_STATUS.PROCESSING,
  WORK_ORDER_STATUS.CLOSED,
  WORK_ORDER_STATUS.ABORTED,
];

export function canWorkOrderHaveRepairs(workOrderStatus) {
  return REPAIRABLE_PARENT_STATUSES.includes(workOrderStatus);
}

function toQtyOrZero(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/** 結單前檢查：問題數＝已修復＋不維修。 */
export function isWorkOrderRepairQtyBalanced(values) {
  const problem = toQtyOrZero(values?.problemQty);
  const repaired = toQtyOrZero(values?.repairedQty);
  const notRepaired = toQtyOrZero(values?.notRepairedQty);
  // 六位小數內比較，避免浮點誤差
  return Math.round(problem * 1e6) === Math.round((repaired + notRepaired) * 1e6);
}

function formatWarehouseLabel(warehouse) {
  if (!warehouse) {
    return '';
  }
  return [warehouse.code, warehouse.name].filter(Boolean).join(' ');
}

/** 庫存挑選列 → 維修用料列（新列無 id；單位待 options 就緒後由 UI 帶預設）。 */
export function mapInvStockRowToRepairMaterial(row) {
  if (!row?.id) {
    return null;
  }
  return {
    id: '',
    inventoryStockId: row.id,
    stockNo: row.stockNo ?? '',
    mtrId: row.mtr?.id ?? '',
    mtrNo: row.mtr?.mtrNo ?? '',
    mtrName: row.mtr?.name ?? '',
    warehouseId: row.warehouse?.id ?? '',
    warehouseLabel: formatWarehouseLabel(row.warehouse),
    availableQty: getInvStockAvailableQty(row),
    unitId: '',
    unitOptions: extractUnitOptions(row.mtr ?? row),
    qty: '',
  };
}

/** detail material → 表單列（名稱由 UI 依 mtrId 查；顯示單位用快照）。 */
function mapRepairMaterialToFormRow(material) {
  return {
    id: material?.id ?? '',
    inventoryStockId: material?.inventoryStockId ?? '',
    stockNo: '',
    mtrId: material?.mtrId ?? '',
    mtrNo: '',
    mtrName: '',
    warehouseId: material?.warehouseId ?? '',
    warehouseLabel: '',
    availableQty: null,
    unitId: material?.unitId ?? '',
    unitOptions: [],
    qty: material?.qty == null ? '' : String(material.qty),
  };
}

export function createEmptyWorkOrderRepairFormValues() {
  return {
    problemQty: '',
    repairedQty: '0',
    notRepairedQty: '0',
    problemDescription: '',
    handlingDescription: '',
    remark: '',
    materials: [],
  };
}

export function mapWorkOrderRepairToFormValues(repair) {
  if (!repair) {
    return createEmptyWorkOrderRepairFormValues();
  }
  const qtyText = (value) => (value == null ? '' : String(value));
  return {
    problemQty: qtyText(repair.problemQty),
    repairedQty: qtyText(repair.repairedQty),
    notRepairedQty: qtyText(repair.notRepairedQty),
    problemDescription: repair.problemDescription ?? '',
    handlingDescription: repair.handlingDescription ?? '',
    remark: repair.remark ?? '',
    materials: (repair.materials ?? []).map(mapRepairMaterialToFormRow),
  };
}

/** 已過帳（有 OUT transaction）且尚未被更正沖回的用料。 */
export function listCorrectableRepairMaterials(repair, corrections = []) {
  const reversed = new Set(
    (corrections ?? []).flatMap((correction) => (
      (correction?.lines ?? []).map((line) => line?.repairMaterialId)
    )),
  );
  return (repair?.materials ?? []).filter((material) => (
    material?.inventoryTransactionId && !reversed.has(material.id)
  ));
}

const ERROR_MESSAGES = {
  WORK_ORDER_REPAIR_PARENT_NOT_APPROVED: '工單須為生產中、已結案或已中止才能開維修單',
  WORK_ORDER_REPAIR_NOT_OPEN: '維修單已結單，無法修改',
  WORK_ORDER_REPAIR_NOT_CLOSED: '維修單尚未結單，無法更正',
  WORK_ORDER_REPAIR_QTY_UNBALANCED: '問題數量須等於已修復數量＋不維修數量才能結單',
  WORK_ORDER_REPAIR_QUANTITY_SCALE_INVALID: '數量最多 6 位小數',
  WORK_ORDER_REPAIR_STOCK_BASE_UNIT_MISMATCH: '所選庫存的基本單位與物料不符',
  WORK_ORDER_REPAIR_UNIT_CONVERSION_UNAVAILABLE: '此單位無法換算，請改選其他單位',
  WORK_ORDER_REPAIR_CONVERSION_RATE_NOT_INTEGER: '單位換算倍率須為正整數，請改選其他單位',
  WORK_ORDER_REPAIR_MATERIAL_SOURCE_INVALID: '所選庫存不可用（須為啟用且可用），請重新選擇',
  WORK_ORDER_REPAIR_MATERIAL_SOURCE_CHANGED: '庫存來源已變更，請重新載入後再試',
  WORK_ORDER_REPAIR_CORRECTION_IDEMPOTENCY_CONFLICT: '此更正已用不同內容送出過，請重新開啟更正視窗',
  WORK_ORDER_REPAIR_CORRECTION_MATERIAL_NOT_OWNED: '所選用料不屬於此維修單',
  WORK_ORDER_REPAIR_CORRECTION_LINES_REQUIRED: '請至少選擇一筆要沖回的用料',
  WORK_ORDER_REPAIR_REVERSAL_ALREADY_POSTED: '所選用料已沖回過',
  WORK_ORDER_REPAIR_REVERSAL_STOCK_CHANGED: '庫存在扣帳後已有其他異動，無法沖回',
  WORK_ORDER_REPAIR_REVERSAL_ORDER_UNPROVABLE: '無法確認庫存交易順序，無法沖回',
  WORK_ORDER_REPAIR_PREDECESSOR_UNKNOWN: '舊資料缺少扣帳前序紀錄，無法沖回',
  REVISION_REQUIRED: '缺少版本，請重新載入後再試',
};

export function resolveWorkOrderRepairErrorMessage(code) {
  return ERROR_MESSAGES[code] ?? null;
}

export function getWorkOrderRepairErrorMessage(error, fallback) {
  const code = error?.response?.data?.code;
  return resolveWorkOrderRepairErrorMessage(code) ?? getWorkOrderApiErrorMessage(error, fallback);
}
